import { createContext, useContext, useEffect, useState, useCallback } from "react";
import apiClient from "../../services/api";

// Shape: { assignmentSource: 'TRACKER' | 'SITUATION' }
// Default to TRACKER so the UI starts in read-only mode until the API
// returns a definitive value — this is the safer failure posture
// (no accidental writes enabled by a slow-loading flag).
const DEFAULT_SETTINGS = { assignmentSource: "TRACKER" };

const SystemSettingsContext = createContext(null);

export function useSystemSettings() {
  const ctx = useContext(SystemSettingsContext);
  if (!ctx) {
    // Return defaults instead of throwing so components used outside
    // the provider (e.g. login page) still render sensibly.
    return { ...DEFAULT_SETTINGS, loading: false, refresh: () => {} };
  }
  return ctx;
}

export function SystemSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await apiClient.getSystemSettings();
      if (data && typeof data.assignmentSource === "string") {
        setSettings({ assignmentSource: data.assignmentSource });
      }
    } catch {
      // Leave the default in place; surface via UI only if needed.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SystemSettingsContext.Provider value={{ ...settings, loading, refresh }}>
      {children}
    </SystemSettingsContext.Provider>
  );
}
